import type {
  PassengerInsurance,
  PassengerInsuranceClaim
} from '@/types/motor/MotorInsurance'
import type { MonetaryAmount } from 'policies-api'

export interface TariffInformation {
  title?: string
  tariff?: string
  comprehensionAsString?: string
  formattedPrice?: string
}

export interface ChangeVehicleApplication {
  policyId: string
  insuranceStartsAt: string
  circleOfDrivers: CircleOfDrivers
  numberOfFullyComprehensiveInsuranceClaimsCurrentYear: number
  numberOfLiabilityClaimsCurrentYear: number
  vehicleMileage: number
  vehicleFirstRegistration: string
  vehicleAcquiredAt: string
  vehicleTypeKey: string
  vehicleManufacturerId: string
}

export interface ChangeVehicleConclusion {
  applicationDate: string
  changeVehicleRequest: ChangeVehicleApplication
}

export interface CompleteChangeVehicleOffer {
  motorQuote: MotorQuote
  additionalServices: AdditionalServices
}

export interface AdditionalServices {
  lexLiabilityInsurances: InsuranceExtra[]
  lexPartiallyComprehensiveInsurances: InsuranceExtra[]
  lexFullyComprehensiveInsurances: InsuranceExtra[]
}

export interface MotorQuote {
  quoteDate: string
  monetaryAmountPassengersInsurance: MonetaryAmount
  monetaryAmountAssistanceCover: MonetaryAmount
  premiumRate: MonetaryAmount
  tariffRate: MonetaryAmount

  motorQuoteLEXLiabilityInsurance: InsuranceExtra[]
  motorQuoteLEXPartiallyComprehensiveInsurance: InsuranceExtra[]
  motorQuoteLEXFullyComprehensiveInsurance: InsuranceExtra[]

  motorApplication: MotorApplication
}

export interface MotorApplication {
  paymentInterval: PaymentPeriod
  insuranceCover: InsuranceCover
  previousContract: PreviousContract
}

export interface PreviousContract {
  numberOfLiabilityClaimsCurrentYear: number
  numberOfLiabilityClaimsLastYear: number
}

export interface InsuranceCover {
  product: Product
  seasonalInsurance: boolean
  insuredVehicleUsage: UsageOfVehicle
  comprehensiveInsurance: ComprehensiveInsurance
  passengerInsurance?: PassengerInsuranceClaim
  assistanceCover: boolean
  insuranceStartsAt: string
  motorVehicleLiability: MotorVehicleLiability
}

export interface ComprehensiveInsurance {
  '@type': ComprehensiveInsuranceType
}

export interface MotorVehicleLiability {
  noClaimsClass: string
  sumInsuredFlatRate: MonetaryAmount
  sumInsuredPerPerson: MonetaryAmount
}

export interface UsageOfVehicle {
  mileageAYear: number
  nightlyParkingPlace: NightlyParkingPlace
  typeOfUse: TypeOfUse
  drivers: CircleOfDrivers
}

export interface CircleOfDrivers {
  circleOfDriversType: CircleOfDriversType
  partnerBirthyear?: string
  youngestDriverBirthyear?: string
  oldestDriverBirthyear?: string
  partnerGender?: Gender
  youngestDriverGender?: Gender
}

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE'
}

export enum ComprehensiveInsuranceType {
  FULL = 'FullyComprehensiveInsurance',
  PARTIAL = 'PartiallyComprehensiveInsurance'
}

export enum Product {
  BASIC = 'Basic',
  COMFORT = 'Comfort'
}

export enum PaymentPeriod {
  YEARLY = 'YEARLY',
  HALF_YEARLY = 'HALF_YEARLY',
  QUARTERLY = 'QUARTERLY',
  MONTHLY = 'MONTHLY'
}

export interface InsuranceExtra {
  lex: Lex
  amount?: MonetaryAmount
}

export enum Lex {
  HAFTPFLICHT_RABATTSCHUTZ = 'HAFTPFLICHT_RABATTSCHUTZ',
  FAHRERSCHUTZ = 'FAHRERSCHUTZ',
  MARDERBISSFOLGESCHAEDEN_BIS_3000EUR = 'MARDERBISSFOLGESCHAEDEN_BIS_3000EUR',
  ZUSAMMENSTOSS_MIT_ALLEN_TIEREN = 'ZUSAMMENSTOSS_MIT_ALLEN_TIEREN',
  VERSICHERUNGSSCHUTZ_BEI_GROBER_FAHRLAESSIGKEIT = 'VERSICHERUNGSSCHUTZ_BEI_GROBER_FAHRLAESSIGKEIT',
  KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN_COMFORT = 'KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN_COMFORT',
  FREIE_WERKSTATTWAHL = 'FREIE_WERKSTATTWAHL',
  ERSATZWAGEN = 'ERSATZWAGEN',
  TIERBISSFOLGESCHAEDEN_GEMAESS_AKB = 'TIERBISSFOLGESCHAEDEN_GEMAESS_AKB',
  VOLLKASKO_RABATTSCHUTZ = 'VOLLKASKO_RABATTSCHUTZ',
  ERWEITERTE_NEUWERTENTSCHAEDIGUNG = 'ERWEITERTE_NEUWERTENTSCHAEDIGUNG',
  GAPDeckung = 'GAPDeckung'
}

export enum TypeOfUse {
  MOSTLY_PRIVATE = 'MOSTLY_PRIVATE',
  MOSTLY_COMMERCIAL = 'MOSTLY_COMMERCIAL',
  COMMERCIAL = 'COMMERCIAL'
}

export enum CircleOfDriversType {
  EINZELFAHRER = 'EINZELFAHRER',
  VN_UND_PARTNER = 'VN_UND_PARTNER',
  VN_UND_ANDERE = 'VN_UND_ANDERE'
}

export enum NightlyParkingPlace {
  EINZEL_DOPPELGARAGE = 'EINZEL_DOPPELGARAGE',
  PRIVATES_GRUNDSTUECK = 'PRIVATES_GRUNDSTUECK',
  SAMMELGARAGE = 'SAMMELGARAGE',
  OEFFENTLICHE_STRASSE = 'OEFFENTLICHE_STRASSE'
}

export interface InsuranceProtection {
  overallAmount?: MonetaryAmount
  tariffAmount?: MonetaryAmount
  motorCoverageLiabilityInsurance?: InsuranceExtra[]
  motorCoveragePartiallyComprehensiveInsurance?: InsuranceExtra[]
  motorCoverageFullyComprehensiveInsurance?: InsuranceExtra[]
  additionalServiceLiabilityInsurance?: InsuranceExtra[]
  additionalServicePartiallyComprehensiveInsurance?: InsuranceExtra[]
  additionalServiceFullyComprehensiveInsurance?: InsuranceExtra[]
  assistanceCoverAmount?: MonetaryAmount
  assistanceCover?: boolean
  paymentPeriod?: string
  passengerInsurance?: PassengerInsurance
  comprehensiveInsuranceType?: ComprehensiveInsuranceType
  sumInsuredFlatRate?: string
  sumInsuredPerPerson?: string
}

export interface InsuranceData {
  manufacturer?: string
  modelType?: string
  manufacturerId?: string
  typeKey?: string
  power?: string
  vehicleFirstRegistration?: string
  vehicleFirstRegistrationForPartner?: string
  vehicleDriver?: string
  usageOfVehicle?: string
  kilometersAYear?: string
  postalCode?: string
  nightlyParkingSpace?: string
  numberOfDamages?: string
  seasonVehicleLicence?: string
  commencementOfInsurance?: string
  noClaimsClass?: string
}

export interface MotorConclusionResponse {
  conclusionNumber: string
  evb: string
}

export enum VehicleChangeState {
  VEHICLE_SELECTION,
  PRICE_INFORMATION,
  CONCLUSION
}

export enum ChangeType {
  CALL_CENTER = 'CALL_CENTER',
  ONE_CLICK = 'ONE_CLICK'
}
